import Chip from '@components/v2/ui/Chip/Chip';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import LangEnum from '../../../../lang/LangEnum';
import { CatalogTestIds } from '../../../../utils/testIds/catalog.testIds';
import { getAvailabilityLangEnum } from '../../../products/ProductAvailabilityRenderer';
import { AvailabilityEnum } from '../../../products/ProductTypes';
import { useTranslation } from '../../../translation/TranslationContext';
import { CriteriaOperatorEnum } from '../../enums/criteriaOperator.enum';
import {
  CATALOG_CREATION_CRITERIA_CHIP_HEIGHT,
  CATALOG_CREATION_CRITERIA_OPERATOR_LABELS,
  CATALOG_CREATION_CRITERIA_TOOLTIP_MAX_WIDTH,
} from '../catalogCreationCriteria.consts';
import {
  CatalogCreationCriterionChip,
  CatalogCreationCriterionEnum,
  CatalogCreationCriterionOptionLabelsById,
} from '../catalogCreationCriteria.types';
import {
  buildCatalogCreationCriterionPhrase,
  groupCatalogCreationCriterionLabels,
  joinCatalogCreationCriterionLabelGroups,
} from '../catalogCreationCriteria.utils';

interface CatalogCreationCriteriaChipProps {
  chip: CatalogCreationCriterionChip;
  optionLabelsById: CatalogCreationCriterionOptionLabelsById;
  onEdit?: (criterion: CatalogCreationCriterionEnum, element: HTMLElement) => void;
  onRemove?: (criterion: CatalogCreationCriterionEnum) => void;
}

const CatalogCreationCriteriaChip = ({
  chip,
  optionLabelsById,
  onEdit,
  onRemove,
}: CatalogCreationCriteriaChipProps) => {
  const { translate } = useTranslation();

  const availabilityLabels = getAvailabilityLangEnum(false);

  const resolveValueLabel = (value: string) => {
    if (chip.criterion === CatalogCreationCriterionEnum.AVAILABILITY) {
      return translate(availabilityLabels[value as AvailabilityEnum]);
    }

    return optionLabelsById[value]?.name ?? value;
  };

  const criterionLabel = translate(chip.phraseLabel, { count: chip.values.length });
  const outerOperatorLabel = translate(CATALOG_CREATION_CRITERIA_OPERATOR_LABELS[chip.operator]);

  const isGroupedByClassificationType =
    chip.criterion === CatalogCreationCriterionEnum.CLASSIFICATION &&
    chip.operator === CriteriaOperatorEnum.AND;

  const labelGroups = isGroupedByClassificationType
    ? groupCatalogCreationCriterionLabels(
        chip.values.map((value) => ({
          label: resolveValueLabel(value),
          groupKey: optionLabelsById[value]?.groupId ?? value,
        })),
      )
    : [chip.values.map(resolveValueLabel)];

  const innerOperatorLabel = isGroupedByClassificationType
    ? translate(CATALOG_CREATION_CRITERIA_OPERATOR_LABELS[CriteriaOperatorEnum.OR])
    : outerOperatorLabel;

  const phrase = buildCatalogCreationCriterionPhrase({
    labelGroups,
    innerOperatorLabel,
    outerOperatorLabel,
    remainingLabel: translate(LangEnum.GLOBAL_REMAINING_SELECTED_COUNT, {
      quantity: chip.values.length - 1,
    }),
  });

  const fullPhrase = joinCatalogCreationCriterionLabelGroups({
    labelGroups,
    innerOperatorLabel,
    outerOperatorLabel,
  });

  return (
    <Tooltip
      tabIndex={-1}
      TooltipContent={
        <Typography
          variant="body2"
          color="inherit"
          sx={{ maxWidth: CATALOG_CREATION_CRITERIA_TOOLTIP_MAX_WIDTH, lineHeight: 'normal' }}
        >
          {`${criterionLabel} ${fullPhrase}`}
        </Typography>
      }
    >
      <Chip
        size="small"
        sx={{ height: CATALOG_CREATION_CRITERIA_CHIP_HEIGHT, paddingInline: 0.5, minWidth: 0 }}
        label={`${criterionLabel} ${phrase}`}
        testId={`${CatalogTestIds.CATALOG_CREATION_MODAL_CRITERIA_CHIP_PREFIX}${chip.criterion}`}
        onClick={onEdit ? (event) => onEdit(chip.criterion, event.currentTarget) : undefined}
        onDelete={onRemove ? () => onRemove(chip.criterion) : undefined}
      />
    </Tooltip>
  );
};

export default CatalogCreationCriteriaChip;
